import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useBlogSettings, resolveBlogLang } from "@/context/BlogContext";
import { SEOHead } from "@/components/SEOHead";
import { BlogHero } from "@/components/blog/BlogHero";
import { BlogCard } from "@/components/blog/BlogCard";
import { BlogSidebar } from "@/components/blog/BlogSidebar";
import { Pagination } from "@/components/blog/Pagination";
import { blogFetch } from "@/lib/blog";

export function BlogCategoryPage() {
  const { slug } = useParams();
  const { lang: uiLang, t } = useLanguage();
  const settings = useBlogSettings();
  const lang = resolveBlogLang(uiLang, settings);
  const [searchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);

  const [data, setData] = useState({ posts: [], total: 0, per_page: 12 });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    Promise.all([
      blogFetch("", { lang, params: { category: slug, page, per_page: 12 }, signal: ctrl.signal }),
      blogFetch("/categories", { lang, signal: ctrl.signal }),
    ])
      .then(([listData, catData]) => {
        setData(listData);
        setCategories(catData.categories || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    return () => ctrl.abort();
  }, [lang, slug, page]);

  const activeCategory = categories.find((c) => c.slug === slug);
  const titleText = activeCategory ? activeCategory.name : t("blog.categoryResults");
  const subtitleText = activeCategory?.description || t("blog.categorySubtitle");

  return (
    <>
      <SEOHead page="blog" titleOverride={titleText} />
      <BlogHero
        title={titleText}
        subtitle={subtitleText}
        categories={categories}
        activeCategorySlug={slug}
        showSearch={false}
      />

      <section className="py-12 sm:py-16 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8">
              {loading && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="rounded-2xl border border-slate-200 bg-white p-6 animate-pulse h-72" />
                  ))}
                </div>
              )}

              {!loading && data.posts.length === 0 && (
                <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
                  <p className="font-medium">{t("blog.empty")}</p>
                </div>
              )}

              {!loading && data.posts.length > 0 && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {data.posts.map((post) => (
                      <BlogCard key={post.id} post={post} lang={lang} />
                    ))}
                  </div>
                  <Pagination
                    page={page}
                    perPage={data.per_page}
                    total={data.total}
                    lang={lang}
                  />
                </>
              )}
            </div>

            <div className="lg:col-span-4">
              <BlogSidebar categories={categories} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
